// Team — the organization's members, workspaces and per-workspace seats
// (decision workspace-tenancy). Org admins/owners only (Navbar hides the
// link; the rules deny everyone else anyway).
//
// Members are seated by email: the backend resolves the uid (POST
// /tenant/:orgId/members) because clients cannot look users up. Workspace
// seats are plain Firestore/pg writes under the generated rules.
import React, { useEffect, useState } from 'react';
import { useAuth } from '../AuthContext';
import { getIdToken } from '../data/authPlane';
import { kitEnv } from '../data/kitEnv';
import {
  useTenant, useEntityList, createEntity, updateEntity, deleteEntity, ROLES, isPrivileged,
} from '../data/entities';

const API = kitEnv('UPLOAD_API', process.env.REACT_APP_UPLOAD_API || '');

async function api(path, body) {
  const token = await getIdToken();
  const res = await fetch(`${API}${path}`, {
    method: body === undefined ? 'GET' : 'POST',
    headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(json.error || `${path}: ${res.status}`);
  return json;
}

const Err = ({ msg }) => (msg ? <p className="cs-help" style={{ color: 'var(--danger, #e5484d)' }}>{msg}</p> : null);

function OrgMembers({ orgId, members, myRole, uid, ownerUid }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [err, setErr] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const canSetOwner = myRole === 'owner';

  const add = async (e) => {
    e.preventDefault();
    if (!email.trim() || busy) return;
    setBusy(true); setErr(''); setNote('');
    try {
      const out = await api(`/tenant/${orgId}/members`, { email: email.trim().toLowerCase(), role });
      if (out.pending) setNote(out.note || 'Seat reserved — applied when they first sign in.');
      setEmail('');
    } catch (ex) { setErr(ex.message); } finally { setBusy(false); }
  };
  const setMemberRole = async (m, r) => {
    setErr('');
    try { await updateEntity('member', { org: orgId }, m.id, { role: r }); } catch (ex) { setErr(ex.message); }
  };
  const remove = async (m) => {
    if (!window.confirm(`Remove ${m.email} from the organization?`)) return;
    setErr('');
    try { await deleteEntity('member', { org: orgId }, m.id); } catch (ex) { setErr(ex.message); }
  };

  return (
    <section className="cs-card" style={{ padding: 16 }}>
      <h2 style={{ margin: '0 0 4px' }}>Organization members</h2>
      <p className="cs-help">Org admins and owners see every workspace. Everyone else needs a seat in a workspace to see what is inside it.</p>
      <form onSubmit={add} style={{ display: 'flex', gap: 8, margin: '12px 0', flexWrap: 'wrap' }}>
        <input className="cs-input" style={{ maxWidth: 300 }} type="email" placeholder="person@company.com" value={email} onChange={(e) => setEmail(e.target.value)} />
        <select className="cs-input cs-select" value={role} onChange={(e) => setRole(e.target.value)} style={{ maxWidth: 140 }}>
          {ROLES.filter((r) => r !== 'owner').map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <button className="cs-btn cs-btn-primary cs-btn-sm" type="submit" disabled={!email.trim() || busy}>Add member</button>
      </form>
      <Err msg={err} />
      {note && <p className="cs-help">{note}</p>}
      <table className="cs-table" style={{ width: '100%' }}>
        <thead><tr><th>Member</th><th>Role</th><th /></tr></thead>
        <tbody>
          {(members || []).map((m) => {
            const isOwnerRow = m.id === ownerUid;
            return (
              <tr key={m.id}>
                <td>{m.email}{m.id === uid ? ' (you)' : ''}</td>
                <td>
                  {isOwnerRow ? <span className="cs-chip">owner</span> : (
                    <select className="cs-input cs-select" value={m.role} onChange={(e) => setMemberRole(m, e.target.value)} style={{ maxWidth: 140 }}>
                      {ROLES.filter((r) => r !== 'owner' || canSetOwner).map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                  )}
                </td>
                <td style={{ textAlign: 'right' }}>
                  {!isOwnerRow && <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => remove(m)} data-agent-noclick>Remove</button>}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

// Who may create their own account on this instance (backend
// shared/signupPolicy.js). Closed by default; 'open' is a plan entitlement
// the control plane grants (Kit Business) — the backend refuses it otherwise.
const SIGNUP_LABELS = {
  closed: ['Invite only', 'Only people you add above can sign in. Default.'],
  domains: ['Company domains', 'Anyone with an email at the domains below can create an account.'],
  open: ['Open to the public', 'Anyone can create an account.'],
};

function MembershipPolicy({ orgId }) {
  const [policy, setPolicy] = useState(null);
  const [signup, setSignup] = useState('closed');
  const [domains, setDomains] = useState('');
  const [err, setErr] = useState('');
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    api(`/tenant/${orgId}/access`).then((p) => {
      if (!live) return;
      setPolicy(p); setSignup(p.signup); setDomains((p.domains || []).join(', '));
    }).catch((ex) => live && setErr(ex.message));
    return () => { live = false; };
  }, [orgId]);

  const save = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(''); setSaved(false);
    try {
      const p = await api(`/tenant/${orgId}/access`, { signup, signupDomains: domains.split(/[\s,;]+/).filter(Boolean) });
      setPolicy(p); setSignup(p.signup); setDomains((p.domains || []).join(', ')); setSaved(true);
    } catch (ex) { setErr(ex.message); } finally { setBusy(false); }
  };

  if (!policy && !err) return null;
  const dirty = policy && (signup !== policy.signup || domains !== (policy.domains || []).join(', '));
  return (
    <section className="cs-card" style={{ padding: 16 }}>
      <h2 style={{ margin: '0 0 4px' }}>Membership</h2>
      <p className="cs-help">Who can create an account on this app. Sign-in is always limited to members; this only controls self-sign-up.</p>
      {policy && (
        <form onSubmit={save} style={{ display: 'grid', gap: 8, margin: '12px 0' }}>
          {(policy.modes || Object.keys(SIGNUP_LABELS)).map((m) => {
            const locked = m === 'open' && !policy.entitled;
            return (
              <label key={m} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', opacity: locked ? 0.6 : 1 }}>
                <input type="radio" name="signup" value={m} checked={signup === m} disabled={locked} onChange={() => setSignup(m)} style={{ marginTop: 4 }} />
                <span>
                  <strong>{SIGNUP_LABELS[m][0]}</strong>
                  {locked && <span className="cs-chip" style={{ marginLeft: 8 }}>Kit Business</span>}
                  <br />
                  <span className="cs-help">{locked ? 'Included with Kit Business — upgrade this app on nventr.io to open it to everyone.' : SIGNUP_LABELS[m][1]}</span>
                </span>
              </label>
            );
          })}
          {signup === 'domains' && (
            <input className="cs-input" style={{ maxWidth: 420, marginLeft: 26 }} placeholder="example.com, example.org" value={domains} onChange={(e) => setDomains(e.target.value)} />
          )}
          {policy.signup === 'open' && policy.effective !== 'open' && (
            <p className="cs-help">Public sign-up is selected but not included in this app’s plan, so the app is currently invite-only.</p>
          )}
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button className="cs-btn cs-btn-primary cs-btn-sm" type="submit" disabled={!dirty || busy}>{busy ? 'Saving…' : 'Save'}</button>
            {saved && !dirty && <span className="cs-help">Saved — takes effect immediately.</span>}
          </div>
        </form>
      )}
      <Err msg={err} />
    </section>
  );
}

function WorkspaceSeats({ orgId, ws, members, myRole, uid }) {
  const { docs: seats } = useEntityList('workspaceMember', { org: orgId, workspace: ws.id });
  const [pick, setPick] = useState('');
  const [role, setRole] = useState('member');
  const [err, setErr] = useState('');
  const seatedIds = new Set((seats || []).map((s) => s.id));
  const candidates = (members || []).filter((m) => !seatedIds.has(m.id) && !isPrivileged(m.role));

  const seat = async (e) => {
    e.preventDefault();
    const m = (members || []).find((x) => x.id === pick);
    if (!m) return;
    setErr('');
    try {
      await createEntity('workspaceMember', { org: orgId, workspace: ws.id }, {
        uid: m.id, email: m.email, displayName: m.displayName || '', role, addedBy: uid,
      }, { id: m.id });
      setPick('');
    } catch (ex) { setErr(ex.message); }
  };
  const setSeatRole = async (s, r) => {
    setErr('');
    try { await updateEntity('workspaceMember', { org: orgId, workspace: ws.id }, s.id, { role: r }); } catch (ex) { setErr(ex.message); }
  };
  const unseat = async (s) => {
    setErr('');
    try { await deleteEntity('workspaceMember', { org: orgId, workspace: ws.id }, s.id); } catch (ex) { setErr(ex.message); }
  };
  const rename = async () => {
    const name = window.prompt('Workspace name', ws.name);
    if (!name || name === ws.name) return;
    try { await updateEntity('workspace', { org: orgId }, ws.id, { name }); } catch (ex) { setErr(ex.message); }
  };
  const remove = async () => {
    if (!window.confirm(`Delete workspace "${ws.name}"? Seats are removed; items stay org-wide only if you move them first.`)) return;
    try { await deleteEntity('workspace', { org: orgId }, ws.id); } catch (ex) { setErr(ex.message); }
  };

  return (
    <div className="cs-card" style={{ padding: 14 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <strong style={{ flex: 1 }}>{ws.name}</strong>
        <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={rename}>Rename</button>
        {isPrivileged(myRole) && <button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={remove} data-agent-noclick>Delete</button>}
      </div>
      {ws.description && <p className="cs-help">{ws.description}</p>}
      <form onSubmit={seat} style={{ display: 'flex', gap: 8, margin: '10px 0', flexWrap: 'wrap' }}>
        <select className="cs-input cs-select" value={pick} onChange={(e) => setPick(e.target.value)} style={{ maxWidth: 280 }}>
          <option value="">Seat an org member…</option>
          {candidates.map((m) => <option key={m.id} value={m.id}>{m.email}</option>)}
        </select>
        <select className="cs-input cs-select" value={role} onChange={(e) => setRole(e.target.value)} style={{ maxWidth: 140 }}>
          {ROLES.filter((r) => r !== 'owner' || isPrivileged(myRole)).map((r) => <option key={r} value={r}>{r}</option>)}
        </select>
        <button className="cs-btn cs-btn-outline cs-btn-sm" type="submit" disabled={!pick}>Seat</button>
      </form>
      <Err msg={err} />
      {(seats || []).length === 0 ? (
        <p className="cs-help">No seats yet — org admins/owners already see this workspace.</p>
      ) : (
        <table className="cs-table" style={{ width: '100%' }}>
          <tbody>
            {(seats || []).map((s) => (
              <tr key={s.id}>
                <td>{s.email}</td>
                <td>
                  <select className="cs-input cs-select" value={s.role} onChange={(e) => setSeatRole(s, e.target.value)} style={{ maxWidth: 140 }}>
                    {ROLES.filter((r) => r !== 'owner' || isPrivileged(myRole)).map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                </td>
                <td style={{ textAlign: 'right' }}><button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => unseat(s)}>Unseat</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// AI clients over MCP (backend api/mcpServer.js + api/tokensApi.js). Every
// member may mint tokens for their own Cursor / Claude Code; admins also see
// everyone's. OAuth-capable clients (Claude Desktop, claude.ai, ChatGPT)
// need no token — they sign in through /oauth/authorize and show up here
// as grants.
const SCOPE_HELP = { read: 'read — look up records, ask the knowledge base', write: 'read + write — also create, update and delete what your seat allows' };
const fmtDate = (ms) => (ms ? new Date(ms).toLocaleDateString() : '—');

function CopyBlock({ label, text }) {
  const [done, setDone] = useState(false);
  const copy = () => { navigator.clipboard?.writeText(text).then(() => { setDone(true); setTimeout(() => setDone(false), 1500); }); };
  return (
    <div>
      <div className="u-cluster" style={{ justifyContent: 'space-between', marginBottom: 'var(--sp-1)' }}>
        <span className="cs-help u-small">{label}</span>
        <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={copy}>{done ? 'Copied' : 'Copy'}</button>
      </div>
      <code className="cs-code" style={{ fontSize: 'var(--fs-xs)' }}>{text}</code>
    </div>
  );
}

function AiClients({ isAdmin, uid }) {
  const [info, setInfo] = useState(null);
  const [tokens, setTokens] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [name, setName] = useState('');
  const [scope, setScope] = useState('read');
  const [days, setDays] = useState(90);
  const [minted, setMinted] = useState(null);
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = React.useCallback(() => api(`/tokens${showAll ? '?all=1' : ''}`).then((r) => setTokens(r.tokens || [])).catch((ex) => setErr(ex.message)), [showAll]);
  useEffect(() => { api('/mcp/info').then(setInfo).catch((ex) => setErr(ex.message)); }, []);
  useEffect(() => { load(); }, [load]);

  const mint = async (e) => {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true); setErr(''); setMinted(null);
    try {
      const out = await api('/tokens', { name: name.trim(), scopes: scope === 'write' ? ['read', 'write'] : ['read'], ttlDays: Number(days) || 90 });
      setMinted(out); setName(''); load();
    } catch (ex) { setErr(ex.message); } finally { setBusy(false); }
  };
  const revoke = async (tk) => {
    if (!window.confirm(`Revoke "${tk.name || tk.prefix}"? Any client using it stops working now.`)) return;
    setErr('');
    try {
      const token = await getIdToken();
      const res = await fetch(`${API}/tokens/${tk.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
      if (!res.ok) throw new Error((await res.json().catch(() => ({}))).error || `${res.status}`);
      if (minted?.token?.id === tk.id) setMinted(null);
      load();
    } catch (ex) { setErr(ex.message); }
  };

  const url = info?.url || '…';
  const secret = minted?.secret || '<paste your token>';
  const cursorJson = JSON.stringify({ mcpServers: { [info?.app?.name?.toLowerCase().replace(/[^a-z0-9]+/g, '-') || 'app']: { url, headers: { Authorization: `Bearer ${secret}` } } } }, null, 2);
  const claudeCmd = `claude mcp add --transport http ${info?.app?.name?.toLowerCase().replace(/[^a-z0-9]+/g, '-') || 'app'} ${url} --header "Authorization: Bearer ${secret}"`;

  return (
    <section className="cs-card" style={{ padding: 16 }}>
      <h2 style={{ margin: '0 0 4px' }}>AI clients (MCP)</h2>
      <p className="cs-help">
        This app is an MCP server. Cursor, Claude Code, Claude Desktop and ChatGPT can look up and (with a write token) change the records your seat allows — the same tools the in-app assistant has, under the same permissions, with every call audited.
        {info && !info.enabled && <strong> The server is switched off (config mcp.enabled).</strong>}
      </p>
      {info && (
        <div style={{ display: 'grid', gap: 'var(--sp-3)', margin: 'var(--sp-3) 0' }}>
          <CopyBlock label="MCP server URL — OAuth clients (Claude Desktop, claude.ai connectors, ChatGPT) need only this; they sign in with your account." text={url} />
          <details>
            <summary className="cs-help" style={{ cursor: 'pointer' }}>Token-based setup (Cursor mcp.json · Claude Code)</summary>
            <div style={{ display: 'grid', gap: 'var(--sp-3)', marginTop: 'var(--sp-2)' }}>
              <CopyBlock label="Cursor — .cursor/mcp.json" text={cursorJson} />
              <CopyBlock label="Claude Code" text={claudeCmd} />
            </div>
          </details>
          <p className="cs-help u-small">Tools visible to you: {info.toolCount?.read} read{info.allowWrites ? ` · ${info.toolCount?.write} with write scope` : ' · writes disabled by the operator'}.</p>
        </div>
      )}

      <form onSubmit={mint} style={{ display: 'flex', gap: 8, margin: '12px 0', flexWrap: 'wrap', alignItems: 'center' }}>
        <input className="cs-input" style={{ maxWidth: 220 }} placeholder="Token name (e.g. Cursor on laptop)" value={name} onChange={(e) => setName(e.target.value)} />
        <select className="cs-input cs-select" value={scope} onChange={(e) => setScope(e.target.value)} style={{ maxWidth: 320 }} aria-label="Scope">
          <option value="read">{SCOPE_HELP.read}</option>
          {info?.allowWrites !== false && <option value="write">{SCOPE_HELP.write}</option>}
        </select>
        <label className="cs-help u-small">
          expires in{' '}
          <input className="cs-input" type="number" min={1} max={info?.pat?.maxTtlDays || 365} value={days} onChange={(e) => setDays(e.target.value)} style={{ width: 80, display: 'inline-block' }} /> days
        </label>
        <button className="cs-btn cs-btn-primary cs-btn-sm" type="submit" disabled={!name.trim() || busy || info?.enabled === false}>Create token</button>
      </form>
      {minted && (
        <div className="cs-banner b-ok" role="status" style={{ display: 'grid', gap: 'var(--sp-2)' }}>
          <span>Token created — copy it now, it will not be shown again.</span>
          <CopyBlock label={minted.token?.name} text={minted.secret} />
        </div>
      )}
      <Err msg={err} />

      <div className="u-cluster" style={{ justifyContent: 'space-between', marginTop: 'var(--sp-3)' }}>
        <span className="cs-help u-small">{showAll ? 'Everyone\'s tokens and grants' : 'Your tokens and grants'}</span>
        {isAdmin && <button type="button" className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => setShowAll((v) => !v)}>{showAll ? 'Show mine' : 'Show everyone\'s'}</button>}
      </div>
      {tokens.length === 0 ? (
        <p className="cs-empty" style={{ marginTop: 'var(--sp-2)' }}>No tokens or grants yet — create a token above, or connect an OAuth client and it appears here.</p>
      ) : (
        <div className="u-scroll-x">
          <table className="cs-table" style={{ width: '100%' }}>
            <thead><tr><th>Name</th><th>Kind</th><th>Scope</th>{showAll && <th>Member</th>}<th>Last used</th><th>Expires</th><th /></tr></thead>
            <tbody>
              {tokens.map((tk) => (
                <tr key={tk.id}>
                  <td>{tk.name || tk.prefix}<span className="cs-help u-xs" style={{ marginLeft: 6 }}>{tk.prefix}…</span></td>
                  <td><span className="cs-chip">{tk.kind === 'oauth' ? 'OAuth grant' : 'token'}</span></td>
                  <td>{tk.scopes.join(' + ')}</td>
                  {showAll && <td className="u-truncate" style={{ maxWidth: 160 }}>{tk.uid === uid ? 'you' : tk.uid}</td>}
                  <td>{tk.lastUsedAt ? new Date(tk.lastUsedAt).toLocaleString() : 'never'}</td>
                  <td>{tk.kind === 'oauth' ? 'refreshes' : fmtDate(tk.expiresAt)}</td>
                  <td style={{ textAlign: 'right' }}><button className="cs-btn cs-btn-ghost cs-btn-sm" onClick={() => revoke(tk)} data-agent-noclick>Revoke</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

export default function Team() {
  const { user } = useAuth();
  const t = useTenant();
  const orgId = t.orgId;
  const { docs: members } = useEntityList('member', orgId ? { org: orgId } : {}, { orderBy: 'createdAt', dir: 'asc', enabled: !!orgId });
  const [wsName, setWsName] = useState('');
  const [err, setErr] = useState('');
  const org = t.orgs.find((o) => o.id === orgId);

  if (!orgId) return <div className="cs-page"><p className="cs-help">{t.loaded ? 'You are not a member of an organization yet.' : 'Loading…'}</p></div>;
  if (!isPrivileged(t.role)) {
    // Members still own their AI-client tokens; the rest of the team page is admin work.
    return (
      <div className="cs-page" style={{ display: 'grid', gap: 16 }}>
        <div><h1 style={{ marginBottom: 4 }}>Team</h1><p className="cs-help">Only organization admins manage members and workspaces.</p></div>
        <AiClients isAdmin={false} uid={user.uid} />
      </div>
    );
  }

  const createWs = async (e) => {
    e.preventDefault();
    if (!wsName.trim()) return;
    setErr('');
    try {
      const id = await createEntity('workspace', { org: orgId }, { name: wsName.trim(), createdBy: user.uid });
      // Creator takes the owner seat (admins already pass; this makes the
      // workspace survive a later demotion of its creator).
      await createEntity('workspaceMember', { org: orgId, workspace: id }, {
        uid: user.uid, email: (user.email || '').toLowerCase(), role: 'owner', addedBy: user.uid,
      }, { id: user.uid }).catch(() => {});
      setWsName('');
    } catch (ex) { setErr(ex.message); }
  };

  const ownerUid = (members || []).find((m) => m.role === 'owner')?.id;

  return (
    <div className="cs-page" style={{ display: 'grid', gap: 16 }}>
      <div>
        <h1 style={{ marginBottom: 4 }}>Team</h1>
        <p className="cs-help">
          Organization <strong>{orgId}</strong>{org ? ` · you are ${org.role}` : ''}. Workspaces segregate items, pipelines and agents; a person only sees the workspaces they are seated in.
        </p>
      </div>
      <OrgMembers orgId={orgId} members={members} myRole={t.role} uid={user.uid} ownerUid={ownerUid} />
      <MembershipPolicy orgId={orgId} />
      <AiClients isAdmin uid={user.uid} />
      <section className="cs-card" style={{ padding: 16 }}>
        <h2 style={{ margin: '0 0 4px' }}>Workspaces</h2>
        <p className="cs-help">New items, pipelines and agents land in the workspace selected in the top bar (or org-wide when “All workspaces” is selected).</p>
        <form onSubmit={createWs} style={{ display: 'flex', gap: 8, margin: '12px 0' }}>
          <input className="cs-input" style={{ maxWidth: 300 }} placeholder="New workspace name" value={wsName} onChange={(e) => setWsName(e.target.value)} />
          <button className="cs-btn cs-btn-primary cs-btn-sm" type="submit" disabled={!wsName.trim()}>Create workspace</button>
        </form>
        <Err msg={err} />
        <div style={{ display: 'grid', gap: 12 }}>
          {(t.workspaces || []).map((ws) => (
            <WorkspaceSeats key={ws.id} orgId={orgId} ws={ws} members={members} myRole={t.role} uid={user.uid} />
          ))}
          {t.workspaces?.length === 0 && <p className="cs-help">No workspaces yet. Everything is org-wide.</p>}
        </div>
      </section>
    </div>
  );
}
